import { formatMinor } from '@/kova/format'
import type {
  BalLeg, Block, CardItem, CardsBlock, NoteBlock, Source, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'
import { exactMinor, isCurrencyCode, type MinorWire } from '@/lib/money/money'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Dashboard › Today, read from `GET /v1/admin/dashboard/today` (go-wallet#231).
 *
 * # Why this file is mostly about absence
 *
 * The backend goes to unusual trouble to distinguish three things a naive API
 * would collapse into one number: a queue that is genuinely empty, a queue
 * whose table does not exist yet, and a queue that exists and could not be
 * read. Every count and every figure arrives with an `availability` beside it,
 * and a non-available one arrives with NO value at all — the field is absent
 * from the wire rather than zero on it.
 *
 * That distinction only pays off if the console preserves it. An operator scans
 * this screen for non-zero counts in the first thirty seconds of a shift; a
 * fabricated zero reads as "nothing needs attention" and is the single most
 * dangerous thing this screen can render. So nothing here substitutes a zero,
 * substitutes the design corpus, or quietly drops a card it cannot fill: an
 * unreadable figure renders as unreadable, carrying the backend's reason.
 *
 * # And mostly about not doing arithmetic
 *
 * The delta between our ledger and the institution's record arrives computed,
 * for the same reason. A console subtracting it would have to decide what to do
 * when one side is missing, and the answer it would reach for is to treat the
 * missing side as zero — turning "we could not read the institution's record"
 * into "the institution shows nothing there" (invariant 2).
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_dashboard.go json tags
// ---------------------------------------------------------------------------

/**
 * Whether a figure has a value at all.
 *
 * `not_built` and `unavailable` are deliberately not one state: the first is a
 * roadmap fact and the second is an incident, and an operator does something
 * different about each.
 */
export type Availability = 'available' | 'not_built' | 'unavailable'

/** Where a figure's truth comes from. Invariant 2, made visible per figure. */
export type Authority = 'operational' | 'provider-confirmed' | 'analytics'

export interface ProvenanceView {
  readonly source: string
  readonly authority: string
  readonly legal: string
  readonly read_at: string
}

export interface CountView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  /** Absent, never zero, when the count is not available. */
  readonly value?: number
  readonly provenance?: ProvenanceView
}

/** Minor units and an explicit ISO code, always together (invariants 3 and 4). */
export interface MoneyView {
  /**
   * A JSON number OR a decimal string. Both, because the backend is moving
   * between them (go-wallet#377) and this console must read either during the
   * move. `exactMinor` is what turns it into a number, and refuses the values
   * that cannot survive the trip.
   */
  readonly amount_minor: MinorWire
  readonly currency: string
}

export interface FigureView {
  readonly label: string
  readonly availability: string
  readonly reason?: string
  readonly value?: MoneyView
  readonly provenance?: ProvenanceView
}

export interface ReconcilerView {
  readonly feed: string
  readonly state: string
  /** Absent when the feed has never succeeded. A zero timestamp would render as year 1. */
  readonly last_success_at?: string
  readonly age_seconds?: number
  readonly last_error?: string
  readonly needs_attention: boolean
}

export interface MoneyAtRestView {
  readonly currency: string
  readonly ledger: FigureView
  readonly provider: FigureView
  readonly delta: FigureView
  readonly excluded_by_open_cases: FigureView
  readonly reconciled: boolean
}

export interface TodayResponse {
  readonly generated_at: string
  readonly cell: { readonly id: string; readonly role: string }
  readonly attention: number
  readonly needs_a_human: readonly CountView[]
  readonly health: readonly CountView[]
  readonly reconcilers: readonly ReconcilerView[]
  readonly money_at_rest: readonly MoneyAtRestView[]
}

export const DASHBOARD_PATH = '/dashboard/today'

const SOURCE = 'postgres · admin dashboard (GET /v1/admin/dashboard/today)'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

const AVAILABILITIES = new Set<string>(['available', 'not_built', 'unavailable'])

/**
 * An availability this console does not know becomes `unavailable`.
 *
 * The same posture `parseRoles` takes in `lib/auth/session.ts`: an unrecognised
 * value from the backend resolves to the reading that shows the operator less
 * confidence, never more. A future fourth state defaulting to "available" would
 * put an unverified number on a money screen.
 */
function availabilityOf(raw: string): Availability {
  return AVAILABILITIES.has(raw) ? (raw as Availability) : 'unavailable'
}

function reasonFor(availability: Availability, raw: string, reason: string | undefined): string {
  if (reason !== undefined && reason !== '') return reason
  if (availability === 'available') return ''
  if (!AVAILABILITIES.has(raw)) {
    return `The backend reported availability "${raw}", which this console does not know. Treated as unreadable.`
  }
  return 'Could not be read. This is not a count of zero.'
}

/**
 * An integer we can state exactly, or nothing.
 *
 * `JSON.parse` hands back a double for a Go `int64`, so a value past 2^53 has
 * already lost digits by the time it reaches here. Rendering it anyway would
 * print a number that is wrong in its last places and looks entirely
 * plausible — which on a reconciliation screen is worse than saying we could
 * not read it. Returning `null` routes it into the unreadable path.
 *
 * Delegates to `exactMinor`, which also reads the STRING wire form the backend
 * is moving to (go-wallet#377). One implementation on purpose: this repo had
 * two copies of the rule, in this file and in decision.ts, and the rule has a
 * trap in it — validating a string by `Number()`-ing it first accepts the one
 * value that must be refused. A rule with a trap is a rule to have once.
 */
function exactInteger(value: unknown): number | null {
  return exactMinor(value)
}

/**
 * An RFC-3339 instant in the format the design draws timestamps in.
 *
 * Delegates to `readAtStamp` so every stamp on the console reads the same way,
 * and returns the raw string unchanged rather than inventing one if the backend
 * sends something unparseable — a wrong timestamp beside a balance is worse
 * than an ugly one.
 */
function stamp(iso: string | undefined): string {
  if (iso === undefined || iso === '') return ''
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? iso : readAtStamp(at)
}

/** Compact age, from the server's own arithmetic. No ambient clock here. */
function age(seconds: number): string {
  const s = Math.max(0, Math.trunc(seconds))
  if (s < 60) return `${String(s)}s`
  if (s < 3600) return `${String(Math.floor(s / 60))}m`
  if (s < 86400) return `${String(Math.floor(s / 3600))}h`
  return `${String(Math.floor(s / 86400))}d`
}

function provenanceMeta(p: ProvenanceView | undefined): string {
  if (p === undefined) return ''
  const at = stamp(p.read_at)
  return at === '' ? p.source : `${p.source} · read ${at}`
}

// ---------------------------------------------------------------------------
// Counts → cards
// ---------------------------------------------------------------------------

/**
 * One queue, as a card.
 *
 * The three availabilities have to be distinguishable at a glance and not only
 * on inspection, so each moves three things at once: the count, the posture
 * word, and the tone. A real zero is `0 · clear · good`; an unreadable count is
 * `— · unreadable · critical`. They cannot be confused, which is the whole
 * reason the endpoint carries the distinction.
 *
 * `screenId` is left empty — the card links nowhere. The destinations in the
 * design corpus are corpus data; inventing one here would send an operator to a
 * screen that may not answer for this figure.
 */
function countCard(c: CountView): CardItem {
  const availability = availabilityOf(c.availability)
  const reason = reasonFor(availability, c.availability, c.reason)
  const meta = provenanceMeta(c.provenance)

  if (availability === 'available') {
    const n = exactInteger(c.value)
    if (n === null) {
      // Available with no usable value is a contradiction, and the safe side of
      // it is to say so rather than to print the half of it we trust.
      return [c.label, '—', '', 'unreadable', 'r',
        'The backend reported this count as available and sent no value we can state exactly.',
        meta, '']
    }
    return n === 0
      ? [c.label, '0', '', 'clear', 'g',
          'A read count of zero. This is an empty queue, not a failed read.', meta, '']
      : [c.label, String(n), '', 'waiting', 'a', '', meta, '']
  }

  return availability === 'not_built'
    // Neutral, and never green: a queue nobody has built has not been checked.
    ? [c.label, '—', '', 'not built', 'n', reason, meta, '']
    : [c.label, '—', '', 'unreadable', 'r', reason, meta, '']
}

// ---------------------------------------------------------------------------
// Reconcilers → cards on the health band
// ---------------------------------------------------------------------------

/**
 * A reconciler's posture, by state.
 *
 * `never_ran` is critical and says so in words. A feed with zero runs has
 * proven nothing — it is not a clean run, and the failure mode this guards
 * against is a reconciler that was registered, never scheduled, and read as
 * green for a month because it had no findings to report (go-wallet#88).
 *
 * `inert` is the same shape of lie one layer in: it runs, it succeeds, and it
 * has never examined a record.
 */
const RECONCILER_TONE: Readonly<Record<string, Tone>> = {
  ok: 'g',
  stale: 'a',
  inert: 'a',
  failing: 'r',
  never_ran: 'r',
}

const RECONCILER_POSTURE: Readonly<Record<string, string>> = {
  ok: 'within window',
  stale: 'stale',
  inert: 'inert',
  failing: 'failing',
  never_ran: 'unproven',
}

const RECONCILER_NOTE: Readonly<Record<string, string>> = {
  ok: '',
  stale: 'It has succeeded before, but not recently enough. The job stopped; the scheduler is where to look.',
  inert: 'It runs and succeeds and has never examined a single record. A feed reading an empty table looks identical to a feed with nothing to find.',
  failing: 'The job is running on schedule and its work is broken.',
  never_ran: 'Zero runs since the feed was registered. Never-run is not a clean run — this reconciler has proven nothing.',
}

function reconcilerCard(r: ReconcilerView): CardItem {
  const known = Object.prototype.hasOwnProperty.call(RECONCILER_TONE, r.state)
  // Two sources for the same judgement, and the backend's flag wins. If it says
  // a feed wants a human, no state mapping here may render it green — including
  // a state added to the backend after this console was built.
  const mapped: Tone = known ? (RECONCILER_TONE[r.state] ?? 'r') : 'r'

  // No age means no success to age FROM. Never a zero and never "just now":
  // 'never / ran' is the reading the design corpus uses and the only honest one.
  const seconds = exactInteger(r.age_seconds)
  const [count, unit] = seconds === null ? ['never', 'ran'] : [age(seconds), 'ago']

  // And no age is no proven run, whatever the backend called the state. A card
  // reading "never / ran" beside "within window" in the calm tone is the exact
  // reconciler that goes unlooked-at for a month (#88), so the absence of an
  // age vetoes green here for the same reason `needs_attention` does. Both are
  // one-directional: they can take green away and never grant it.
  const unproven = seconds === null
  const tone: Tone = (r.needs_attention || unproven) && mapped === 'g' ? 'a' : mapped

  const posture = unproven && mapped === 'g'
    ? 'unproven'
    : known ? (RECONCILER_POSTURE[r.state] ?? r.state) : r.state
  const note = known ? (RECONCILER_NOTE[r.state] ?? '') : 'A reconciler state this console does not know.'

  const sub = r.last_error !== undefined && r.last_error !== ''
    ? (note === '' ? `Last error: ${r.last_error}` : `${note} Last error: ${r.last_error}`)
    : note

  const at = stamp(r.last_success_at)
  const meta = at === '' ? `${r.feed} · no successful run` : `${r.feed} · last success ${at}`

  return [`${r.feed} reconciler`, count, unit, posture, tone, sub, meta, '']
}

// ---------------------------------------------------------------------------
// Money at rest → one balance block per currency
// ---------------------------------------------------------------------------

/** The figures of one currency's position, in the order they are read. */
function figuresOf(m: MoneyAtRestView): readonly FigureView[] {
  return [m.ledger, m.provider, m.delta, m.excluded_by_open_cases]
}

interface Rendered {
  readonly legs: readonly BalLeg[]
  /** Labels that could not become legs, each with the reason it could not. */
  readonly withheld: readonly string[]
}

/** A figure that can be a leg, or the reason it cannot be one. */
type Placement =
  | { readonly ok: true; readonly minor: number; readonly currency: string }
  | { readonly ok: false; readonly reason: string }

/**
 * Whether one figure may be placed on the balance, and if not, why not.
 *
 * ONE decision, read by both the block and the screen's own provenance chip, so
 * the two cannot disagree. Three of the refusals below fire while the backend
 * says `available` — a law-12 analytics authority, a value it did not send, an
 * amount with no currency — and while this logic lived only inside the block
 * each of them dropped a leg quietly and left the screen badging itself `Live`,
 * which claims that everything on the screen was read. A reconciliation missing
 * one of its sides is the last place that claim may be made.
 */
function placeFigure(f: FigureView): Placement {
  const availability = availabilityOf(f.availability)
  if (availability !== 'available') {
    return { ok: false, reason: reasonFor(availability, f.availability, f.reason) }
  }

  // Available with no amount is a contradiction, and the safe side of it is to
  // say so. Falling through to `reasonFor` produced the empty string, so the
  // operator was shown "Delta:" with nothing after the colon.
  if (f.value === undefined) {
    return {
      ok: false,
      reason:
        'The backend reported this figure as available and sent no amount. This is not a balance of zero.',
    }
  }

  // Law 12. An analytics-sourced balance is not a balance this console is
  // permitted to render at any tone — the mirror is eventually consistent and
  // never a source of truth. Withheld rather than badged, because there is no
  // freshness stamp that would make it safe to compare against the ledger.
  if (f.provenance?.authority === 'analytics') {
    return {
      ok: false,
      reason:
        'refused — reported as analytics-sourced, and no balance on this console may come from the analytics mirror (law 12).',
    }
  }

  const minor = exactInteger(f.value.amount_minor)
  if (minor === null) {
    return {
      ok: false,
      reason: 'the amount arrived beyond exact integer range, so it cannot be stated.',
    }
  }

  // The FULL currency predicate, not `currency !== ''`.
  //
  // `formatMinor` throws on anything that is not three letters, and it is right
  // to — an amount whose currency we cannot name is not a value this system may
  // carry (invariant 4). But it is called during render, so a `"EU"` or a
  // `"eur"` off the wire did not produce a withheld leg: it produced a thrown
  // exception inside `Bal` and took the whole reconciliation screen to the
  // error boundary. Checking only the empty string caught the one malformed
  // value that happened to be in the fixtures and none of the others.
  //
  // Asked here, before a block is built, so a currency we cannot state becomes
  // a named absence like every other one.
  if (!isCurrencyCode(f.value.currency)) {
    return {
      ok: false,
      reason:
        f.value.currency === ''
          ? 'the amount arrived with no currency at all, so it cannot be stated (invariant 4).'
          : `the amount arrived with ${JSON.stringify(f.value.currency)}, which is not an ISO-4217 code, so it cannot be stated (invariant 4).`,
    }
  }

  // Normalised, not passed through. `money()` upper-cases before it validates,
  // so `"eur"` is a perfectly good code — but the ISO span beside the amount
  // renders this string verbatim, and an operator comparing two legs should not
  // have to decide whether `eur` and `EUR` are the same currency. Canonical
  // here means the block cannot render them differently.
  return { ok: true, minor, currency: f.value.currency.trim().toUpperCase() }
}

/**
 * Turns one currency's figures into balance legs.
 *
 * A leg structurally requires a number, so a figure without one cannot be a
 * leg. It is not dropped: it comes back in `withheld` and is rendered beside
 * the block as prose. Silently shortening the block would leave the operator
 * comparing two sides of a reconciliation without being told a third figure
 * exists and is missing.
 *
 * Tone here is the verdict, not the provenance — the tag carries which record
 * answered. Neither side of a comparison is "good" on its own; only their
 * agreement is.
 */
function renderPosition(m: MoneyAtRestView): Rendered {
  const legs: BalLeg[] = []
  const withheld: string[] = []

  for (const f of figuresOf(m)) {
    const placed = placeFigure(f)
    if (!placed.ok) {
      withheld.push(`${f.label}: ${placed.reason}`)
      continue
    }

    const isDelta = f === m.delta
    const isExcluded = f === m.excluded_by_open_cases
    const tone: Tone = isDelta ? (placed.minor === 0 ? 'g' : 'r') : isExcluded ? 'a' : 'n'
    const tag = f.provenance?.authority ?? 'unattributed'
    const sub = provenanceMeta(f.provenance)

    legs.push(isExcluded
      ? [f.label, placed.minor, placed.currency, sub, tag, tone, tone, 'excl']
      : [f.label, placed.minor, placed.currency, sub, tag, tone, tone])
  }

  return { legs, withheld }
}

/**
 * The verdict line under the arithmetic.
 *
 * Read from the delta on screen rather than from `reconciled` alone: if the two
 * ever disagree, the number the operator can see wins, and an unreadable delta
 * is never "tied". Unknown is not agreement.
 */
function bindingLine(m: MoneyAtRestView, at: string): string {
  const delta = m.delta
  if (availabilityOf(delta.availability) !== 'available' || delta.value === undefined) {
    return `Not tied — the difference is unknown, not zero. As of ${at}.`
  }
  const minor = exactInteger(delta.value.amount_minor)
  if (minor === null) return `Not tied — the difference could not be stated exactly. As of ${at}.`
  if (minor === 0 && m.reconciled) return `Tied as of ${at}.`
  return `Not tied — delta ${formatMinor(minor, delta.value.currency)} ${delta.value.currency}, as of ${at}.`
}

function positionBlocks(m: MoneyAtRestView, at: string): Block[] {
  const { legs, withheld } = renderPosition(m)
  const out: Block[] = [{
    t: 'bal',
    title: `Money at rest — ${m.currency}`,
    src: 'ledger',
    note: 'Σ customer wallets must equal the institution’s record. Per currency, never blended.',
    legs,
    formula: 'Σ wallet/* == provider_settlement + Σ pending_out',
    binding: bindingLine(m, at),
  }]

  if (withheld.length > 0) {
    // Beside the block, not inside it, and never instead of it. The reason
    // travels with the label because "—" tells an operator nothing they can act
    // on, and the difference between "#103 has not been built" and "the read
    // failed" decides whether they page anyone.
    const note: NoteBlock = {
      t: 'note',
      tone: 'a',
      kicker: 'Not shown',
      title: `${m.currency} — ${String(withheld.length)} figure(s) could not be placed on the balance`,
      body: withheld.join(' · '),
      ref: 'A missing figure is not a figure of zero.',
    }
    out.push(note)
  }
  return out
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * The band's source badge, derived from the figures rather than asserted.
 *
 * A hardcoded "ledger" badge would keep saying ledger on the day something
 * analytics-sourced appears in the band, which is precisely the case law 12
 * exists to make visible. Analytics carries a freshness stamp with it; the
 * other two do not need one because they are read on the request.
 */
function bandSource(items: readonly { readonly provenance?: ProvenanceView }[]): {
  src?: Source
  fresh?: string
} {
  const authorities = new Set<string>()
  let latest = ''
  for (const i of items) {
    if (i.provenance === undefined) continue
    authorities.add(i.provenance.authority)
    if (i.provenance.read_at > latest) latest = i.provenance.read_at
  }
  if (authorities.has('analytics')) {
    return { src: 'analytics', fresh: `fresh ${stamp(latest)}` }
  }
  if (authorities.size === 1 && authorities.has('provider-confirmed')) return { src: 'provider' }
  if (authorities.size === 0) return {}
  return { src: 'ledger' }
}

function cardsBlock(
  title: string,
  note: string,
  counts: readonly CountView[],
  items: readonly CardItem[],
): CardsBlock {
  const { src, fresh } = bandSource(counts)
  return {
    t: 'cards',
    title,
    note,
    ...(src === undefined ? {} : { src }),
    ...(fresh === undefined ? {} : { fresh }),
    items,
  }
}

function isTodayResponse(payload: unknown): payload is TodayResponse {
  if (typeof payload !== 'object' || payload === null) return false
  const r = payload as Partial<TodayResponse>
  return Array.isArray(r.needs_a_human) && Array.isArray(r.health)
    && Array.isArray(r.reconcilers) && Array.isArray(r.money_at_rest)
}

/** What the operator is not being told, for the screen's own provenance chip. */
function missingFrom(t: TodayResponse): string[] {
  const missing: string[] = []
  for (const c of [...t.needs_a_human, ...t.health]) {
    const a = availabilityOf(c.availability)
    if (a === 'not_built') missing.push(`${c.label} (not built)`)
    else if (a === 'unavailable') missing.push(`${c.label} (could not be read)`)
  }
  for (const m of t.money_at_rest) {
    for (const f of figuresOf(m)) {
      // Asked of the same function the block asks, not of the availability
      // alone: a figure the block refused to place is a figure the operator was
      // not told, whatever the backend called it.
      if (placeFigure(f).ok) continue
      const a = availabilityOf(f.availability)
      missing.push(
        a === 'not_built'
          ? `${m.currency} ${f.label} (not built)`
          : a === 'unavailable'
            ? `${m.currency} ${f.label} (could not be read)`
            : `${m.currency} ${f.label} (withheld)`,
      )
    }
  }
  return missing
}

function provenanceOf(t: TodayResponse, missing: readonly string[]): DataSource {
  const authorities = new Set<string>()
  for (const c of [...t.needs_a_human, ...t.health]) {
    if (c.provenance !== undefined) authorities.add(c.provenance.authority)
  }
  for (const m of t.money_at_rest) {
    for (const f of figuresOf(m)) {
      if (f.provenance !== undefined) authorities.add(f.provenance.authority)
    }
  }

  const authority = authorities.size === 0
    ? 'unattributed'
    : [...authorities].sort((a, b) => a.localeCompare(b)).join(' + ')
  const readAt = stamp(t.generated_at)

  // `partial` is the normal state of this screen today, not an error one: six
  // of the queues §6.1 specifies have not been built. Saying so in the chip is
  // what stops the screen reading as a complete picture of the shift.
  return missing.length === 0
    ? { kind: 'live', source: SOURCE, authority, readAt }
    : { kind: 'partial', source: SOURCE, authority, readAt, missing }
}

/**
 * Folds the response into the blocks the screen renders.
 *
 * Pure, and separate from the fetch, so the whole mapping — including every
 * availability path — is table-testable without a client (CLAUDE.md Part III).
 */
export function composeDashboard(t: TodayResponse): ScreenData {
  const at = stamp(t.generated_at)

  const blocks: Block[] = [
    cardsBlock(
      'Needs a human now',
      at === '' ? 'Operational counts' : `Operational counts · read ${at}`,
      t.needs_a_human,
      t.needs_a_human.map(countCard),
    ),
    cardsBlock(
      'Health',
      'Worker and provider posture · stale reporting is not an outage',
      t.health,
      [...t.health.map(countCard), ...t.reconcilers.map(reconcilerCard)],
    ),
  ]

  for (const m of t.money_at_rest) blocks.push(...positionBlocks(m, at))

  if (t.money_at_rest.length === 0) {
    // An absent band is not a reconciled one. Without this the screen would end
    // after Health and read as though every currency tied.
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Money at rest',
      title: 'No currency position was returned',
      body: 'Neither our ledger nor the institution’s record answered with a position in any currency, so nothing is reconciled here — including nothing being wrong.',
      ref: 'invariant 2 · reconciliation position',
    })
  }

  const missing = missingFrom(t)
  return { blocks, provenance: provenanceOf(t, missing) }
}

/**
 * The screen when the endpoint itself could not be read.
 *
 * One note and nothing else. The alternative — falling back to the design
 * corpus — would put invented counts and an invented reconciliation position on
 * a screen an operator reads to decide whether anything is wrong, which is the
 * exact failure this endpoint was shaped to prevent.
 *
 * The provenance is `unread`, not `partial`. It used to be `partial` with a
 * `missing` list naming "every figure on Today", which was accurate in the
 * tooltip and wrong in the chip: the chip renders one word, "Partial", and that
 * word says SOME of this is real. Nothing here is. There is no `readAt` on the
 * variant at all, so the old empty-string stamp cannot be reintroduced by
 * someone filling in a blank field.
 */
function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  const ref = decisionRef === null ? 'Nothing on this screen was read.' : `decision_ref ${decisionRef}`
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Today',
      title: 'This screen could not be read',
      body: `${reason} No count, reconciler state or balance below is known — that is different from all of them being clear, and the screen shows none rather than guessing at any.`,
      ref,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

/**
 * Reads Today.
 *
 * Resolves rather than throwing, per the loader contract: a backend that said
 * "I don't know" is a `partial` the operator can read, not an exception that
 * collapses the screen to a stack trace. A refusal keeps its `decision_ref`
 * (law 13) — the client surfaces it and a screen must not swallow it.
 */
export const loadDashboard: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(DASHBOARD_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The dashboard endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    // An AbortError is a navigation away, not a failure to report. Let it
    // propagate so the caller's cancellation stays a cancellation.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }

  if (!isTodayResponse(payload)) {
    return unreadableScreen('The dashboard endpoint returned a shape this console does not know.', null)
  }
  return composeDashboard(payload)
}
